import {Injectable} from '@angular/core';
import {BehaviorSubject} from "rxjs";
import {DbResponse} from "../shared/interceptors/db-response";
import {PaymentMethodMapper} from "../user/payment-method-mapper";
import {RequesterService} from "../shared/services/requester/requester.service";
import {PaymentMethod} from "../user/payment-method";
import {UserSubscription} from "../user/user-subscription";
import {PaymentMapper} from "../user/payment-mapper";
import {NewSubscriptionMapper} from "../user/new-subscription-mapper";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";
import {Store} from "@ngrx/store";
import * as fromStore from "../store";
import {clientRoles} from "../user/user-roles-actions";
import {WorkspaceMapper} from "../user-managment/workspace.mapper";
import {RouteDataService} from "../shared/services/route-data/route-data.service";
import {Router} from "@angular/router";
import { Observable, Subject, combineLatest} from "rxjs";
import {first, map} from "rxjs/operators";
import {TypeUtils} from "../shared/utils/type-utils";
import * as userActions from "../store/actions/user.action";
import {SwitchWorkspace} from "../store";
import * as websiteActions from "../store/actions/website.action";
import {UserManagementService} from "../user-managment/user-management.service";
import {GlobalEventDispatcherService} from "../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {CookieService} from "ngx-cookie";

@Injectable()
export class WorkspaceService {

  private ownerWorkspaceId: any;
  private currentWorkspaceId: any;
  private currentWorkspaceRoleId: any;

  constructor(
    private _router: Router,
    private _requester: RequesterService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _store: Store<fromStore.DbStoreState>,
    private _userManagementService: UserManagementService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe( workspaceIdEntities => {
        this.currentWorkspaceRoleId = workspaceIdEntities.currentWorkspaceRoleId;
        this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
        this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
    });
  }

  public getPaymentMethods(updateCache: boolean = false): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/agency/credit-cards`, null, updateCache)
      .pipe( map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.map(PaymentMethodMapper.map);
        }
        return response;
      })));
  }

  public getActivePaymentMethod(updateCache: boolean = false): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this.getPaymentMethods(updateCache).pipe(
      map((response: DbResponse): DbResponse => {
        if (response && response.success) {
          response.data = response.data.find((paymentMethod: PaymentMethod): boolean => paymentMethod.active);
        }

        return response;
      }))
    );
  }


  public setActivePaymentMethod(paymentMethodId: number): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/agency/credit-cards/${paymentMethodId}/default`);
  }

  public removePaymentMethod(paymentMethodId: number): BehaviorSubject<DbResponse> {
    return this._requester.core
      .delete(`workspaces/${this.currentWorkspaceId}/agency/credit-cards/${paymentMethodId}/delete`);
  }

  public addPaymentMethod(cardData?: any): BehaviorSubject<DbResponse> {
    /* one month free */
/*    if (mode === 'google_pay') {
      checkoutData['stripe_confirm_token'] = stripeToken;
    }
    else {
      checkoutData['payment_method_id'] = stripeToken;
    }*/


    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/agency/credit-cards/create`, null, cardData || null);
  }

  public storePaymentMethod(data: any): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/agency/credit-cards/store-payment-method`, null, data);
  }

  public recharge(paymentMethod: string, paymentMethodId: number): BehaviorSubject<DbResponse> {
    let data = {
      'payment_method': paymentMethod,
    };

    if (paymentMethod === "card") {
      data = Object.assign({}, data, {
        'card_id': paymentMethodId,
      });
    }
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/me/recharge`, null, data);
  }

  public forceCharge(paymentMethod: string, paymentMethodId: number, couponCode?: string, additionalWebsitesCount?: number|null, additionalCdnCount?: number|null, enableCloudflareCdn?: boolean, enableHostedDomain?: boolean, domainInfo?: null, websiteId?: number, period: number = 1): BehaviorSubject<DbResponse> {
    let data = {
      'payment_method': paymentMethod,
    };

    if (paymentMethod === "card") {
      data = Object.assign({}, data, {
        'card_id': paymentMethodId,
      });
    }

    if (additionalWebsitesCount) {
      data = Object.assign({}, data, {
        'count': additionalWebsitesCount,
        'enable_cloudflare_cdn': enableCloudflareCdn ? 1 : 0,
        'cloudflare_cdn_count': enableCloudflareCdn ? additionalCdnCount : 0,
        'enable_hosted_domain': enableHostedDomain ? 1 : 0,
        'hosted_domain_count': enableHostedDomain ? additionalWebsitesCount : 0,
        'hosted_domain_type': 'default',
        'cloudflare_cdn_type': 'inner'
      });
    }

    if (couponCode && !TypeUtils.isNull(couponCode)) {
      data = Object.assign({}, data, {
        'coupon_code': couponCode,
      });
    }

    // Construct the endpoint URL dynamically based on the presence of websiteId
    const endpoint = !!websiteId && !!domainInfo
      ? `workspaces/${this.currentWorkspaceId}/domains/${websiteId}/force-charge`
      : `workspaces/${this.currentWorkspaceId}/user/me/force-charge`;

    return this._requester.core
      .post(endpoint, null, data);
  }
  public purchaseDomain(domainName: string, period: number = 1, websiteId?: number): BehaviorSubject<DbResponse> {
    const data = {
      'domain_name': domainName,
      'period': period
    };

    // Construct the endpoint URL dynamically based on the presence of websiteId
    const endpoint = !!websiteId
      ? `workspaces/${this.currentWorkspaceId}/domains/${websiteId}/domain-names/purchase`
      : `workspaces/${this.currentWorkspaceId}/domain-names/purchase`;

    return this._requester.core
      .post(endpoint, null, data);
  }

  public switchPaymentMethod(method_name: any, cardId?: any): BehaviorSubject<DbResponse> {
    let checkoutData: any = {
      method_name: method_name,
    };
    if (cardId) {
      checkoutData = Object.assign({}, checkoutData, {
        "card_id": cardId
      });
    }
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/payment-methods/switch`, null, checkoutData);
  }

  public getUserSubscription(): BehaviorSubject<DbResponse> {
    const userAccessToken: string = localStorage.getItem('dbUserAccessToken');
    const currentWorkspaceId: string = localStorage.getItem('dbCurrentWorkspaceId');
    const ownerWorkspaceId: string = localStorage.getItem('dbOwnWorkspaceId');
    const currentWorkspaceRoleId: string = localStorage.getItem('dbCurrentWorkspaceRoleId');
    this.currentWorkspaceRoleId = parseInt(currentWorkspaceRoleId && currentWorkspaceRoleId || this.currentWorkspaceRoleId, 10);
    this.ownerWorkspaceId = parseInt(ownerWorkspaceId && ownerWorkspaceId || this.ownerWorkspaceId, 10);
    this.currentWorkspaceId = parseInt(currentWorkspaceId && currentWorkspaceId || this.currentWorkspaceId, 10);
    if (!userAccessToken) {
      const response: DbResponse = new DbResponse();
      response.success = true;
      response.data = null;

      return new BehaviorSubject<DbResponse>(response);
    }
    else {
      return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/user/agreements/last`, null, true);
    }
  }

  public getUserReferralSubscription(start_date?: string | null, end_date?: string | null): BehaviorSubject<DbResponse> {
    return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/user/me/referrals`, null, true);
  }

  public getUserAvailableSubscriptions(): Observable<DbResponse> {
      if (this.currentWorkspaceId) {
          let currentWorkspaceId = this.currentWorkspaceId;
          !currentWorkspaceId && (currentWorkspaceId = localStorage.getItem('dbCurrentWorkspaceId'));
          if ([clientRoles.SITE_DEV, clientRoles.COMPANY_DEV].includes(this.currentWorkspaceRoleId)) {
              currentWorkspaceId = this.ownerWorkspaceId;
              !currentWorkspaceId && (currentWorkspaceId = localStorage.getItem('dbOwnerWorkspaceId'));
          }
          return this._requester.core.get(`workspaces/${currentWorkspaceId}/subscriptions/available-agency-pricing-plans`, null, true)
              .pipe(
                  first(response => !!response),
                  map(response => ({...response, data: {
                          platform: response.data,
                          meta: response.response &&  response.response.body && response.response.body.meta ? response.response.body.meta : {}
                      }
                  }))
              );
      }
    return this._requester.core.get('subscriptions?from_dashboard', null, true)
      .pipe(
          first(response => !!response),
          map(response => ({...response, data: {
              platform: response.data,
              meta: response.response &&  response.response.body && response.response.body.meta ? response.response.body.meta : {}
            }
          }))
      );
  }


  public getPlanDescriptionMap(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core.get('plans_json', null, true));
  }

  public cancelSubscription(userSubscription: UserSubscription, data: any, pause: number = 0): BehaviorSubject<DbResponse> {
    const cancelData: any = {
      cancel_reason: data,
      pause_agreement: pause,
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/${userSubscription.id}/cancel`, null, cancelData);
  }

  public sendCancellationFeedback(userSubscription: UserSubscription, data: any, pause: number = 0): BehaviorSubject<DbResponse> {
    const cancelData: any = {
      cancel_reason: data
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/${userSubscription.id}/cancel-reason`, null, cancelData);
  }


  public activateSubscription(userSubscription: UserSubscription): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/${userSubscription.id}/activate`);
  }

  public renewSubscription(agreementId: number, paymentMethodId?: number, paymentType?: string, subscriptionTitle?: string, domainName = null, period: number = 1): BehaviorSubject<DbResponse> {
    let subscriptionData: any = {
      subscription_id: agreementId,
      payment_type: paymentType,
      domain_period: period
    };

    if (paymentType === "card") {
      subscriptionData = Object.assign({}, subscriptionData, {
        card_id: paymentMethodId
      });
    }
    if (domainName) {
      subscriptionData = Object.assign({}, subscriptionData, {
        domain_name: domainName
      });
    }
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/renew`, null, subscriptionData);
  }

  public upgradeSubscription(subscriptionId: number, additionalWebsitesCount: number|null, couponCode: string, paymentMethodId?: number, paymentType?: string, subscriptionTitle?: string, paypalPaymentId: any = null, paypalPayerId: any = null, productId: number = 0, pricingType?: string, boosterHostingAddOnChecked?: boolean, planId?: number, pricingPlanId?: number, stripePaymentMethodId?: string, upgradeType?: string): BehaviorSubject<DbResponse> {
    let subscriptionData: any = {
      subscription_id: subscriptionId,
      payment_type: paymentType
    };

    if (pricingType && !TypeUtils.isNull(pricingType)) {
      subscriptionData = Object.assign({}, subscriptionData, {
        pricing_type: pricingType
      });
    }

    if (paymentType === "card" && paymentMethodId) {
      subscriptionData = Object.assign({}, subscriptionData, {
        card_id: parseInt(paymentMethodId.toString(), 10)
      });
    }
    if (paymentType === "paypal") {
      subscriptionData = Object.assign({}, subscriptionData, {
        payment_id: paypalPaymentId,
        payer_id: paypalPayerId
      });
    }

    if (additionalWebsitesCount) {
      subscriptionData = Object.assign({}, subscriptionData, {
        count: additionalWebsitesCount
      });
    }

    if (couponCode) {
      subscriptionData = Object.assign({}, subscriptionData, {
        coupon_code: couponCode
      });
    }

    if (productId) {
      subscriptionData = Object.assign({}, subscriptionData, {
        product_id: productId
      });
    }

    if (additionalWebsitesCount) {
      subscriptionData = Object.assign({}, subscriptionData, {
        enable_hosted_domain: 1,
        hosted_domain_count: additionalWebsitesCount,
        hosted_domain_type: 'default'
      });
    }

    if (planId) {
      subscriptionData = Object.assign({}, subscriptionData, {
        plan_id: planId
      });
    }

    if (pricingPlanId) {
      subscriptionData = Object.assign({}, subscriptionData, {
        pricing_plan_id: pricingPlanId
      });
    }

    if (stripePaymentMethodId) {
      subscriptionData = Object.assign({}, subscriptionData, {
        agency_payment_method_id: stripePaymentMethodId,
      });
    }

    if (upgradeType) {
      subscriptionData = Object.assign({}, subscriptionData, {
        upgrade_type: upgradeType
      });
    }

    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/upgrade`, null, subscriptionData);
  }

  public upgradeSubscriptionWithCheckoutSession(subscriptionId: number, additionalWebsitesCount: number|null, couponCode: string, provider: string, paymentProviderCheckoutKey: string, subscriptionTitle?: string, paypalPaymentId: any = null, paypalPayerId: any = null, productId: number = 0, pricingType?: string, boosterHostingAddOnChecked?: boolean, planId?: number): BehaviorSubject<DbResponse> {
    let subscriptionData: any = {
      subscription_id: subscriptionId,
      payment_type: 'agency_suite_customer',
      provider: provider, // 'stripe_connect' or 'paypal'
      payment_provider_checkout_key: paymentProviderCheckoutKey
    };

    if (pricingType && !TypeUtils.isNull(pricingType)) {
      subscriptionData = Object.assign({}, subscriptionData, {
        pricing_type: pricingType
      });
    }

    if (additionalWebsitesCount) {
      subscriptionData = Object.assign({}, subscriptionData, {
        count: additionalWebsitesCount
      });
    }

    if (couponCode) {
      subscriptionData = Object.assign({}, subscriptionData, {
        coupon_code: couponCode
      });
    }

    if (productId) {
      subscriptionData = Object.assign({}, subscriptionData, {
        product_id: productId
      });
    }

    if (additionalWebsitesCount) {
      subscriptionData = Object.assign({}, subscriptionData, {
        enable_hosted_domain: 1,
        hosted_domain_count: additionalWebsitesCount,
        hosted_domain_type: 'default'
      });
    }

    if (planId) {
      subscriptionData = Object.assign({}, subscriptionData, {
        plan_id: planId
      });
    }

    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/upgrade`, null, subscriptionData);
  }

  public addAdditionalService(paymentMethodId, count: number|null = 0, cdnCount: number|null = 0, couponCode: string|null, paymentType = 'stripe', enableCloudflareCdn: boolean, enableHostedDomain: boolean): BehaviorSubject<DbResponse> {
    let subscriptionData: any;
    switch (paymentType) {
      case 'stripe':
        subscriptionData = {
          payment_type: 'card',
          card_id: paymentMethodId
        };
        break;


      case 'paypal_recurring':
      case 'paypal':
        subscriptionData = {
          payment_type: 'paypal_recurring'
        };
        break;

      default:
        subscriptionData = {
          payment_type: 'card',
          card_id: paymentMethodId
        };
        break;
    }

    if (couponCode) {
      subscriptionData = Object.assign({}, subscriptionData, {
        coupon_code: couponCode
      });
    }
    /*TODO make this part clear*/
    subscriptionData = Object.assign({}, subscriptionData, {
      'enable_cloudflare_cdn': enableCloudflareCdn ? 1 : 0,
      'cloudflare_cdn_count': enableCloudflareCdn ? cdnCount : 0,
      'enable_hosted_domain': enableHostedDomain ? 1 : 0,
      'hosted_domain_count': enableHostedDomain ? count : 0,
      'hosted_domain_type': 'default',
      'cloudflare_cdn_type': 'inner'
    });

    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/add-additional-service`, null, subscriptionData);
  }

  public changePaypalAccount( paymentType: any): BehaviorSubject<DbResponse> {
    let checkoutData: any = {
      payment_type: paymentType
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/payment-methods/change-paypal-account`, null, checkoutData);
  }

  public getPayments(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/user/payments/list`, null, true)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.map(PaymentMapper.map);
        }
        return response;
      })));
  }

  public getPaymentInvoiceDownload(paymentId: string): Observable<any> {
    return this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/user/payments/${paymentId}/invoice-download`, null, true);
  }

  public startDownloadingInvoice(invoiceId: number) {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/invoices/${invoiceId}`, null, false, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public upgradeWithCoupon(couponCode: string): BehaviorSubject<DbResponse> {
    const coupon: any = {
      coupon_code: couponCode
    };

    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/upgrade-coupon`, null, coupon);
  }

  public getNextPaymentDateForUpgradableSubscription(subscriptionId): BehaviorSubject<DbResponse> {
    if (this.currentWorkspaceId) {
      return <BehaviorSubject<DbResponse>>this._requester.core.get(`workspaces/${this.currentWorkspaceId}/subscriptions/${subscriptionId}/next-payment-date_auth`, null, true);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`subscriptions/${subscriptionId}/next-payment-date`));
  }

  public getBillingInfo(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.auth
      .get(`workspaces/${this.currentWorkspaceId}/billing-info`))
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = WorkspaceMapper.billingInfoMapper(response.data);
        }
        return response;
      }));
  }

  public updateBillingInfo(billingInfo): BehaviorSubject<DbResponse> {
    const data = WorkspaceMapper.convertToApiFormat(billingInfo);

    return <BehaviorSubject<DbResponse>>(this._requester.auth
      .patch(`workspaces/${this.currentWorkspaceId}/billing-info`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public silentSwitchToOwnWorkspace(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const ownWorkspaceId = +localStorage.getItem('dbOwnWorkspaceId');
      this._userManagementService.switchWorkspace(ownWorkspaceId)
        .pipe(
          first(response => !!response),
        )
        .subscribe(response => {
          if (response.success) {
            this._store.dispatch(new fromStore.SetWorkspaceIdEntities(
              {
                ownerWorkspaceId: ownWorkspaceId,
                currentWorkspaceId: ownWorkspaceId,
                currentWorkspaceRoleId: 1,
                globalEventDispatcherService: this._globalEventDispatcher
              }
            ));

            this._store.dispatch(new userActions.LoadUser());
            this._store.dispatch(new userActions.LoadUserSubscription());
            this._store.dispatch(new userActions.LoadUserAvailableSubscriptions());
            this._store.dispatch(new SwitchWorkspace({switching: false, workspaceName: '', redirect: true}));
            this._store.dispatch(new websiteActions.ClearWebsites());
            this._store.dispatch(new websiteActions.LoadWebsites());
            this._router.navigate(['/']);
            return resolve(true);
          }
          reject();
        });
    });

  }

  public editCoupon(couponCode?): BehaviorSubject<DbResponse> {
    const coupon: any = {
      coupon_code: couponCode ? couponCode : '1wf_50percent_discount'
    };

    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/update`, null, coupon);
  }

  public extendAgreement(days?: number, coupon_code?: string): BehaviorSubject<DbResponse> {
    const data = {};
    if (days) {
      data['days'] = days;
    }
    if (coupon_code) {
      data['coupon_code'] = coupon_code;
    }
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/user/agreements/extend`, null, data || null);
  }

  public updateStripeSubscription(updateData: any): Observable<DbResponse> {
    return this._requester.core.post('stripe/update-subscription', null, updateData)
      .pipe(
        first(response => !!response),
        map(response => {
          if (response.success) {
            // Dispatch actions to refresh user data
            this._store.dispatch(new userActions.LoadUser());
            this._store.dispatch(new userActions.LoadUserSubscription());
            this._store.dispatch(new userActions.LoadUserAvailableSubscriptions());
          }
          return response;
        })
      );
  }

  public toggleSubscription(clientId: string): Observable<any> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/agency/customers/${clientId}/toggle-subscription`, null, true);
  }

    public getAgencyClientsProRatedCredits(clientId: number): Observable<any>  {
        return this._requester.core
            .get(`workspaces/${this.currentWorkspaceId}/agency/customers/${clientId}/calculate-prorated-credit`, null, true);
    }


}
